import { useEffect, useRef, useState } from 'react';

// Reads barcodes with the phone camera (Chrome and Edge on Android, recent Safari). Where the browser cannot,
// it says so; a Bluetooth scanner or typing still works through the page's own scan box.
declare global { interface Window { BarcodeDetector?: any } }

export const cameraScanSupported = () => typeof window !== 'undefined' && 'BarcodeDetector' in window && !!navigator.mediaDevices?.getUserMedia;

export default function BarcodeScanner({ onCode, onClose }: { onCode: (code: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!cameraScanSupported()) { setError('This browser cannot scan with the camera. Type the number or use a Bluetooth scanner.'); return; }
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;
    let lastCode = ''; let lastAt = 0;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        const v = video.current!;
        v.srcObject = stream; await v.play();
        // scratch-off tickets often use Interleaved 2 of 5 ("itf") or Code 128; ask only for what this browser can read
        const wanted = ['ean_13', 'upc_a', 'ean_8', 'upc_e', 'code_128', 'itf'];
        const supported: string[] = (await window.BarcodeDetector!.getSupportedFormats?.()) ?? wanted;
        const detector = new window.BarcodeDetector!({ formats: wanted.filter((f) => supported.includes(f)) });
        timer = window.setInterval(async () => {
          try {
            const found = await detector.detect(v);
            const c = found[0]?.rawValue as string | undefined;
            // the same bottle is ignored for 2 seconds so one scan is one count
            if (c && (c !== lastCode || Date.now() - lastAt > 2000)) { lastCode = c; lastAt = Date.now(); navigator.vibrate?.(40); onCode(c); }
          } catch { /* a frame that cannot be read is skipped */ }
        }, 250);
      } catch {
        setError('Camera blocked. Allow camera access for this site in the browser settings.');
      }
    })();
    return () => { stopped = true; if (timer) clearInterval(timer); stream?.getTracks().forEach((t) => t.stop()); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="cam">
      {error ? <div className="notice bad">{error}</div> : <video ref={video} playsInline muted aria-label="Camera view" />}
      <button onClick={onClose}>Close camera</button>
    </div>
  );
}
